import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useUsers } from "../../context/UsersContext";
import { FaArrowLeft, FaSpinner } from "react-icons/fa";

import Notification from "../Notification";
import DropDown from "../DropDown";

const StudentForm = () => {
  const { id } = useParams(); // Obtener el ID de la URL
  const [formData, setFormData] = useState({
    first_name: "",
    second_name: "",
    first_surname: "",
    second_surname: "",
    correo: "",
    celular: "",
    numero_identificacion: "",
    fecha_nacimiento: "",
    image: null,
    salon_id: "",
  });
  const {
    createNewStudent,
    updateStudentData,
    getUser,
    fetchSalones,
    salones,
  } = useUsers();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false); // Nuevo estado de carga
  const [notification, setNotification] = useState(null);
    
  const closeNotification = () => setNotification(null);

  // ✅ FUNCIÓN para convertir DD-MM-YYYY a YYYY-MM-DD
  const convertirFechaParaInput = (fecha) => {
    if (!fecha) return "";
    const partes = fecha.split("-");
    if (partes.length === 3) {
      const [dia, mes, año] = partes;
      return `${año}-${mes.padStart(2, "0")}-${dia.padStart(2, "0")}`;
    }
    return fecha;
  };

  // ✅ FUNCIÓN para convertir YYYY-MM-DD a DD-MM-YYYY
  const convertirFechaParaBackend = (fecha) => {
    if (!fecha) return "";
    const partes = fecha.split("-");
    if (partes.length === 3) {
      const [año, mes, dia] = partes;
      return `${dia}-${mes}-${año}`;
    }
    return fecha;
  };

  // Memorizar las funciones para evitar dependencias innecesarias en el useEffect
  const fetchSalonesMemo = useCallback(fetchSalones, [fetchSalones]);
  const getUserMemo = useCallback(getUser, [getUser]);

  useEffect(() => {
    const fetchStudent = async () => {
      try {
        if (salones.length === 0) {
          await fetchSalonesMemo();
        }
        if (id) {
          const student = await getUserMemo(id);

          setFormData({
            first_name: student.first_name || "",
            second_name: student.second_name || "",
            first_surname: student.first_surname || "",
            second_surname: student.second_surname || "",
            correo: student.correo || "",
            celular: student.celular || "",
            numero_identificacion: student.numero_identificacion || "",
            fecha_nacimiento: convertirFechaParaInput(student.fecha_nacimiento) || "", // ✅ Convertir formato
            image: null,
            salon_id: student.salon_id ? String(student.salon_id) : "",
          });
        }
      } catch (error) {
      }
    };

    fetchStudent(); // Cargar datos del estudiante si estamos en modo edición
  }, [id, salones.length, fetchSalonesMemo, getUserMemo]);

  // NUEVA FUNCIÓN para manejar cambios del DropDown
  const handleDropdownChange = (name, eventOrValue) => {
    // Extraer el valor del evento si es un objeto
    const value = eventOrValue?.target?.value ?? eventOrValue;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const previewUrl = URL.createObjectURL(file); // Generar URL temporal
      setFormData((prev) => ({ ...prev, image: file, previewUrl })); // Agregar `previewUrl` al estado
    }
  };

  // 🔹 Validar formato DD-MM-YYYY
  const validarFecha = (fecha) => {
    const regex = /^(\d{2})-(\d{2})-(\d{4})$/;
    if (!regex.test(fecha)) return false;

    const [dia, mes, año] = fecha.split("-").map(Number);
    if (mes < 1 || mes > 12) return false;
    if (dia < 1 || dia > 31) return false;
    if (año < 1900 || año > new Date().getFullYear()) return false;

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const data = new FormData();
      
      // ✅ Agregar campos de texto
      data.append("first_name", formData.first_name || "");
      data.append("second_name", formData.second_name || "");
      data.append("first_surname", formData.first_surname || "");
      data.append("second_surname", formData.second_surname || "");
      data.append("correo", formData.correo || "");
      data.append("celular", formData.celular || "");
      data.append("numero_identificacion", formData.numero_identificacion || "");
      
      // ✅ Convertir fecha de YYYY-MM-DD a DD-MM-YYYY para el backend
      const fechaBackend = convertirFechaParaBackend(formData.fecha_nacimiento);
      data.append("fecha_nacimiento", fechaBackend);
      
      // ✅ Manejar salon_id correctamente - EXTRAER VALOR SI ES OBJETO
      let salonValue = formData.salon_id;
      
      // Si es un objeto con target.value, extraer el valor
      if (salonValue && typeof salonValue === 'object' && salonValue.target) {
        salonValue = salonValue.target.value;
      }
      
      
      if (salonValue === "" || salonValue === "null" || salonValue === null || salonValue === undefined) {
        data.append("salon_id", "null");
      } else {
        const salonNumber = parseInt(salonValue, 10);
        if (!isNaN(salonNumber)) {
          data.append("salon_id", salonNumber);
        } else {
          throw new Error("Salón inválido");
        }
      }
      
      // ✅ Agregar imagen solo si existe
      if (formData.image instanceof File) {
        data.append("image", formData.image);
      }

      if (id) {
        await updateStudentData(id, data);
        setNotification({
          type: "success",
          message: "Estudiante actualizado correctamente",
        });
      } else {
        await createNewStudent(data);
        setNotification({
          type: "success",
          message: "Estudiante creado correctamente",
        });
      }
      
      setTimeout(() => {
        navigate("/estudiantes");
      }, 1500);
    } catch (error) {
      setNotification({
        type: "error",
        message: error.response?.data?.message || error.message || "Error al guardar el estudiante",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-gray-100 min-h-screen flex flex-col items-center p-6">
      <button
        className="self-start flex items-center bg-main-bg-sidebar rounded-full hover:bg-blue-800 text-white font-bold px-5 py-3 mb-4"
        onClick={() => navigate(-1)}
      >
        <FaArrowLeft className="mr-2" />
        Volver
      </button>
      <div className="w-full max-w-6xl mx-auto p-8 bg-white rounded-lg shadow-md">
        <h2 className="text-3xl font-bold text-white bg-main-bg-sidebar p-4 rounded-t-lg mb-6 w-full">
          {id ? "Editar Estudiante" : "Agregar Estudiante"}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="flex flex-col md:flex-row gap-6">
            {/* Contenedor de la foto */}
            <div className="flex flex-col items-center justify-start w-full md:w-1/3">
              <label className="block text-blue-900 font-semibold mb-2">
                Foto *
              </label>
              <input
                type="file"
                name="image"
                onChange={handleFileChange}
                className="hidden"
                id="file-upload"
              />
              <label
                htmlFor="file-upload"
                className="cursor-pointer border-2 border-dashed border-gray rounded-lg p-6 w-full text-center text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {formData.previewUrl ? (
                  <img
                    src={formData.previewUrl}
                    alt="Preview"
                    className="max-h-40 mx-auto"
                  />
                ) : (
                  "Subir Foto"
                )}
              </label>
            </div>

            {/* Contenedor del formulario */}
            <div className="w-full md:w-2/3 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block font-bold text-main-bg-sidebar mb-2">
                  Primer Nombre *
                </label>
                <input
                  type="text"
                  name="first_name"
                  placeholder="Primer Nombre"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  required
                  className="w-full border rounded p-2"
                />
              </div>
              <div>
                <label className="block font-bold text-main-bg-sidebar mb-2">
                  Segundo Nombre
                </label>
                <input
                  type="text"
                  name="second_name"
                  placeholder="Segundo Nombre"
                  value={formData.second_name}
                  onChange={handleInputChange}
                  className="w-full border rounded p-2"
                />
              </div>
              <div>
                <label className="block font-bold text-main-bg-sidebar mb-2">
                  Primer Apellido *
                </label>
                <input
                  type="text"
                  name="first_surname"
                  placeholder="Primer Apellido"
                  value={formData.first_surname}
                  onChange={handleInputChange}
                  required
                  className="w-full border rounded p-2"
                />
              </div>
              <div>
                <label className="block font-bold text-main-bg-sidebar mb-2">
                  Segundo Apellido *
                </label>
                <input
                  type="text"
                  name="second_surname"
                  placeholder="Segundo Apellido"
                  value={formData.second_surname}
                  onChange={handleInputChange}
                  required
                  className="w-full border rounded p-2"
                />
              </div>
              <div>
                <label className="block font-bold text-main-bg-sidebar mb-2">
                  Correo *
                </label>
                <input
                  type="email"
                  name="correo"
                  placeholder="Correo"
                  value={formData.correo}
                  onChange={handleInputChange}
                  required
                  className="w-full border rounded p-2"
                />
              </div>
              <div>
                <label className="block font-bold text-main-bg-sidebar mb-2">
                  Celular *
                </label>
                <input
                  type="number"
                  name="celular"
                  placeholder="Celular"
                  value={formData.celular}
                  onChange={handleInputChange}
                  required
                  className="w-full border rounded p-2"
                />
              </div>
              {/* 🔹 NUEVO CAMPO - Número de Identificación */}
              <div>
                <label className="block font-bold text-main-bg-sidebar mb-2">
                  Número de Identificación
                </label>
                <input
                  type="text"
                  name="numero_identificacion"
                  placeholder="TI/CC (Opcional)"
                  value={formData.numero_identificacion}
                  onChange={handleInputChange}
                  className="w-full border rounded p-2"
                />
              </div>
              <div>
                <label className="block font-bold text-main-bg-sidebar mb-2">
                  Fecha de Nacimiento *
                </label>
                <input
                  type="date"
                  name="fecha_nacimiento"
                  value={formData.fecha_nacimiento}
                  onChange={handleInputChange}
                  required
                  className="w-full border rounded p-2"
                />
              </div>
              <div>
                <label className="block font-bold text-main-bg-sidebar mb-2">
                  Salón *
                </label>
                <DropDown
                  name="salon_id"
                  value={formData.salon_id}
                  onChange={(e) => handleDropdownChange("salon_id", e)}
                  options={[
                    { value: "", label: "No Asignado" },
                    ...salones.map((salon) => ({ 
                      value: String(salon.id), // ✅ Convertir a string explícitamente
                      label: `${salon.nombre} - ${salon.grado}` 
                    }))
                  ]}
                />
              </div>
            </div>
          </div>
          <div className="text-right">
            <button
              type="submit"
              className="bg-main-bg-sidebar rounded-full hover:bg-blue-800 text-white font-bold px-6 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={isLoading}
            >
              {isLoading ? <FaSpinner className="animate-spin mr-2" /> : "Guardar"}
            </button>
          </div>
        </form>
      </div>
      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={closeNotification}
        />
      )}
    </div>
  );
};

export default StudentForm;
